import React, { useState, useEffect } from "react";
import {
  supabase,
  User,
  Pharmacy,
  UserPharmacy,
  Tables,
  assign2DigitUserId,
  setUserPassword,
} from "../utils/supabase";
import { useAuth } from "../contexts/AuthContext";
import PinManagement from "../components/PinManagement";
import toast from "react-hot-toast";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  BuildingStorefrontIcon,
  XMarkIcon,
  CheckIcon,
  KeyIcon,
} from "@heroicons/react/24/outline";

interface UserWithPharmacies extends User {
  user_pharmacies: (UserPharmacy & { pharmacy: Pharmacy })[];
}

interface UserInvitation {
  id?: string;
  email: string;
  full_name: string;
  role: "admin" | "employee" | "pharmacist";
  pharmacy_ids: string[];
  pharmacy_role: "admin" | "manager" | "employee" | "pharmacist";
  status: "pending" | "accepted";
  invited_by?: string;
  created_at?: string;
}

interface NewUser {
  email: string;
  full_name: string;
  role: "admin" | "employee" | "pharmacist";
  pharmacy_ids: string[];
  pharmacy_role: "admin" | "manager" | "employee" | "pharmacist";
}

const UserManagement: React.FC = () => {
  const { user: currentUser, pharmacies } = useAuth();
  const [users, setUsers] = useState<UserWithPharmacies[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPinManagement, setShowPinManagement] = useState(false);
  const [editingUser, setEditingUser] = useState<UserWithPharmacies | null>(
    null,
  );
  const [_invitations, setInvitations] = useState<UserInvitation[]>([]);
  const [_newUser, setNewUser] = useState<NewUser>({
    email: "",
    full_name: "",
    role: "employee",
    pharmacy_ids: [],
    pharmacy_role: "employee",
  });
  const [editForm, setEditForm] = useState<{
    full_name: string;
    role: "admin" | "manager" | "employee" | "pharmacist";
    pharmacy_ids: string[];
    pharmacy_role: "admin" | "manager" | "employee" | "pharmacist";
  }>({
    full_name: "",
    role: "employee",
    pharmacy_ids: [],
    pharmacy_role: "employee",
  });
  const [show2DigitIdModal, setShow2DigitIdModal] = useState(false);
  const [selectedUserFor2DigitId, setSelectedUserFor2DigitId] = useState<UserWithPharmacies | null>(null);
  const [new2DigitId, setNew2DigitId] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState<UserWithPharmacies | null>(null);
  const [newPassword, setNewPassword] = useState("");

  // Check if current user is admin
  const isCurrentUserAdmin = currentUser?.role === "admin";

  useEffect(() => {
    if (isCurrentUserAdmin) {
      fetchUsers();
      fetchInvitations();
    }
  }, [isCurrentUserAdmin]);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      // First, get all users
      const { data: usersData, error: usersError } = await supabase
        .from(Tables.USERS)
        .select("*")
        .order("created_at", { ascending: false });

      if (usersError) {
        console.error("Error fetching users:", usersError);
        toast.error("Failed to load users");
        return;
      }

      // Then get user pharmacy assignments with pharmacy details
      const { data: userPharmaciesData, error: userPharmaciesError } =
        await supabase.from(Tables.USER_PHARMACIES).select(`
          *,
          pharmacy:pharmacies(*)
        `);

      if (userPharmaciesError) {
        console.error("Error fetching user pharmacies:", userPharmaciesError);
        toast.error("Failed to load user pharmacy assignments");
        return;
      }

      // Combine the data
      const usersWithPharmacies: UserWithPharmacies[] = usersData.map(
        (user) => ({
          ...user,
          user_pharmacies:
            userPharmaciesData.filter((up) => up.user_id === user.id) || [],
        }),
      );

      setUsers(usersWithPharmacies);
    } catch (error) {
      console.error("Error in fetchUsers:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    toast.error(
      "User creation is disabled. Please create users directly in Supabase Dashboard and then edit them here.",
    );
    setShowCreateModal(false);
  };

  const fetchInvitations = async () => {
    try {
      // This would fetch from a user_invitations table if it exists
      // For now, we'll just set an empty array
      setInvitations([]);
    } catch (error) {
      console.error("Error fetching invitations:", error);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (
      !confirm(
        "Are you sure you want to delete this user? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      setLoading(true);

      // Delete user pharmacy assignments first
      const { error: assignmentError } = await supabase
        .from(Tables.USER_PHARMACIES)
        .delete()
        .eq("user_id", userId);

      if (assignmentError) {
        console.error(
          "Error deleting user pharmacy assignments:",
          assignmentError,
        );
        toast.error("Failed to delete user assignments");
        return;
      }

      // Delete user profile
      const { error: profileError } = await supabase
        .from(Tables.USERS)
        .delete()
        .eq("id", userId);

      if (profileError) {
        console.error("Error deleting user profile:", profileError);
        toast.error("Failed to delete user");
        return;
      }

      toast.success("User deleted successfully");
      await fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error("Failed to delete user");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserRole = async (
    userId: string,
    newRole: "admin" | "manager" | "employee" | "pharmacist",
  ) => {
    try {
      const { error } = await supabase
        .from(Tables.USERS)
        .update({ role: newRole })
        .eq("id", userId);

      if (error) {
        console.error("Error updating user role:", error);
        toast.error("Failed to update user role");
        return;
      }

      toast.success("User role updated successfully");
      await fetchUsers();
    } catch (error) {
      console.error("Error updating user role:", error);
      toast.error("Failed to update user role");
    }
  };

  const _togglePharmacySelection = (pharmacyId: string) => {
    setNewUser((prev) => ({
      ...prev,
      pharmacy_ids: prev.pharmacy_ids.includes(pharmacyId)
        ? prev.pharmacy_ids.filter((id) => id !== pharmacyId)
        : [...prev.pharmacy_ids, pharmacyId],
    }));
  };

  const handleAssign2DigitId = async () => {
    if (!selectedUserFor2DigitId || !new2DigitId.trim()) {
      toast.error("Please enter a valid 2-digit ID");
      return;
    }

    if (!/^\d{2}$/.test(new2DigitId.trim())) {
      toast.error("User ID must be exactly 2 digits");
      return;
    }

    try {
      const result = await assign2DigitUserId(selectedUserFor2DigitId.id, new2DigitId.trim());
      
      if (result.success) {
        toast.success("2-digit user ID assigned successfully");
        setShow2DigitIdModal(false);
        setNew2DigitId("");
        setSelectedUserFor2DigitId(null);
        await fetchUsers();
      } else {
        toast.error(result.error_message || "Failed to assign 2-digit user ID");
      }
    } catch (error) {
      console.error("Error assigning 2-digit user ID:", error);
      toast.error("Failed to assign 2-digit user ID");
    }
  };

  const handleSetPassword = async () => {
    if (!selectedUserForPassword || !newPassword.trim()) {
      toast.error("Please enter a password");
      return;
    }

    if (!currentUser) {
      toast.error("You must be logged in to set passwords");
      return;
    }

    try {
      const result = await setUserPassword(
        selectedUserForPassword.id,
        newPassword.trim(),
        currentUser.id
      );
      
      if (result.success) {
        toast.success("Password set successfully");
        setShowPasswordModal(false);
        setNewPassword("");
        setSelectedUserForPassword(null);
        await fetchUsers();
      } else {
        toast.error(result.error_message || "Failed to set password");
      }
    } catch (error) {
      console.error("Error setting password:", error);
      toast.error("Failed to set password");
    }
  };

  const handleEditUser = (user: UserWithPharmacies) => {
    setEditingUser(user);
    setEditForm({
      full_name: user.full_name,
      role: user.role,
      pharmacy_ids: user.user_pharmacies.map((up) => up.pharmacy_id),
      pharmacy_role:
        user.user_pharmacies.length > 0
          ? user.user_pharmacies[0].role
          : "employee",
    });
  };

  const handleSaveEdit = async () => {
    if (!editingUser) {
      return;
    }

    try {
      setLoading(true);

      // Update user profile
      const { error: profileError } = await supabase
        .from(Tables.USERS)
        .update({
          full_name: editForm.full_name,
          role: editForm.role,
        })
        .eq("id", editingUser.id);

      if (profileError) {
        console.error("Error updating user profile:", profileError);
        toast.error("Failed to update user profile");
        return;
      }

      // Delete existing pharmacy assignments
      const { error: deleteError } = await supabase
        .from(Tables.USER_PHARMACIES)
        .delete()
        .eq("user_id", editingUser.id);

      if (deleteError) {
        console.error("Error deleting pharmacy assignments:", deleteError);
        toast.error("Failed to update pharmacy assignments");
        return;
      }

      // Add new pharmacy assignments
      if (editForm.pharmacy_ids.length > 0) {
        const userPharmacyInserts = editForm.pharmacy_ids.map((pharmacyId) => ({
          user_id: editingUser.id,
          pharmacy_id: pharmacyId,
          role: editForm.pharmacy_role,
        }));

        const { error: assignmentError } = await supabase
          .from(Tables.USER_PHARMACIES)
          .insert(userPharmacyInserts);

        if (assignmentError) {
          console.error(
            "Error creating pharmacy assignments:",
            assignmentError,
          );
          toast.error("Failed to update pharmacy assignments");
          return;
        }
      }

      toast.success("User updated successfully");
      setEditingUser(null);
      await fetchUsers();
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error("Failed to update user");
    } finally {
      setLoading(false);
    }
  };

  const toggleEditPharmacySelection = (pharmacyId: string) => {
    setEditForm((prev) => ({
      ...prev,
      pharmacy_ids: prev.pharmacy_ids.includes(pharmacyId)
        ? prev.pharmacy_ids.filter((id) => id !== pharmacyId)
        : [...prev.pharmacy_ids, pharmacyId],
    }));
  };

  if (!isCurrentUserAdmin) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Access Denied
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            You need administrator privileges to access user management.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">
            User Management
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage users, their roles, and pharmacy assignments. Create users
            manually in Supabase Dashboard, then edit them here.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => setShowPinManagement(true)}
              className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            >
              <KeyIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
              Manage PINs
            </button>
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
            >
              <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
              Create User (Disabled)
            </button>
          </div>
        </div>
      </div>

      {/* Information Banner */}
      <div className="mt-6 rounded-md bg-yellow-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-yellow-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              User Creation Workflow
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                <strong>To add new users:</strong> Create them in Supabase
                Dashboard (Authentication → Users), then return here to edit
                their roles and assign pharmacies.
                <strong>To edit existing users:</strong> Use the edit buttons in
                the table below.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="mt-8 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide"
                      >
                        User
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide"
                      >
                        2-Digit ID
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide"
                      >
                        Global Role
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide"
                      >
                        Pharmacy Assignments
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide"
                      >
                        Created
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0">
                              <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                <UserIcon className="h-6 w-6 text-primary-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.full_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="flex items-center space-x-2">
                            {user.user_id_2digit ? (
                              <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                {user.user_id_2digit}
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                                Not set
                              </span>
                            )}
                            <button
                              onClick={() => {
                                setSelectedUserFor2DigitId(user);
                                setNew2DigitId(user.user_id_2digit || "");
                                setShow2DigitIdModal(true);
                              }}
                              className="text-primary-600 hover:text-primary-900"
                              title="Manage 2-digit ID"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <select
                            value={user.role}
                            onChange={(e) =>
                              handleUpdateUserRole(
                                user.id,
                                e.target.value as
                                  | "admin"
                                  | "manager"
                                  | "employee",
                              )
                            }
                            className="text-sm rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                          >
                            <option value="employee">Employee</option>
                            <option value="manager">Manager</option>
                            <option value="pharmacist">Pharmacist</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            {user.user_pharmacies.map((up) => (
                              <div
                                key={up.id}
                                className="flex items-center space-x-2"
                              >
                                <BuildingStorefrontIcon className="h-4 w-4 text-gray-400" />
                                <span className="text-sm text-gray-900">
                                  {up.pharmacy.name}
                                </span>
                                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
                                  {up.role}
                                </span>
                              </div>
                            ))}
                            {user.user_pharmacies.length === 0 && (
                              <span className="text-sm text-gray-500 italic">
                                No pharmacy assignments
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => {
                                setSelectedUserForPassword(user);
                                setNewPassword("");
                                setShowPasswordModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900"
                              title="Set Password"
                            >
                              <KeyIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleEditUser(user)}
                              className="text-primary-600 hover:text-primary-900"
                              title="Edit User"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            {user.id !== currentUser?.id && (
                              <button
                                onClick={() => handleDeleteUser(user.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete User"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="flex items-center justify-between">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    User Creation Disabled
                  </h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mt-6">
                  <div className="rounded-md bg-blue-50 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-blue-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">
                          How to Create Users
                        </h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <p className="mb-3">
                            User creation through the app is currently disabled.
                            To add new users:
                          </p>
                          <ol className="list-decimal list-inside space-y-1">
                            <li>Go to your Supabase Dashboard</li>
                            <li>Navigate to Authentication → Users</li>
                            <li>Click "Invite a user" or "Add user"</li>
                            <li>Enter their email and send invitation</li>
                            <li>
                              Once they sign up, return here to edit their
                              profile and assign pharmacies
                            </li>
                          </ol>
                          <p className="mt-3 font-medium">
                            You can edit existing users' roles and pharmacy
                            assignments using the edit buttons in the user
                            table.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateUser}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-600 disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <CheckIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                        Got It
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="flex items-center justify-between">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Edit User: {editingUser.email}
                  </h3>
                  <button
                    onClick={() => setEditingUser(null)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mt-6 space-y-4">
                  <div>
                    <label
                      htmlFor="edit_full_name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="edit_full_name"
                      value={editForm.full_name}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          full_name: e.target.value,
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      placeholder="Enter full name"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="edit_role"
                      className="block text-sm font-medium text-gray-700"
                    >
                      User Type
                    </label>
                    <select
                      id="edit_role"
                      value={editForm.role}
                              onChange={(e) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  role: e.target.value as
                                    | "admin"
                                    | "manager"
                                    | "employee"
                                    | "pharmacist",
                                }))
                              }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    >
                      <option value="employee">Medewerker (Employee)</option>
                      <option value="manager">Manager</option>
                      <option value="pharmacist">Pharmacist</option>
                      <option value="admin">Admin (Pharmacy Owner)</option>
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="edit_pharmacy_role"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Pharmacy Role
                    </label>
                    <select
                      id="edit_pharmacy_role"
                      value={editForm.pharmacy_role}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          pharmacy_role: e.target.value as
                            | "admin"
                            | "manager"
                            | "employee"
                            | "pharmacist",
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    >
                      <option value="employee">Employee</option>
                      <option value="manager">Manager</option>
                      <option value="pharmacist">Pharmacist</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assign to Pharmacies
                    </label>
                    <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                      {pharmacies.map((pharmacy) => (
                        <label key={pharmacy.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editForm.pharmacy_ids.includes(
                              pharmacy.id,
                            )}
                            onChange={() =>
                              toggleEditPharmacySelection(pharmacy.id)
                            }
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-900">
                            {pharmacy.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setEditingUser(null)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveEdit}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-600 disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PIN Management Modal */}
      <PinManagement
        isOpen={showPinManagement}
        onClose={() => setShowPinManagement(false)}
      />

      {/* 2-Digit ID Management Modal */}
      {show2DigitIdModal && selectedUserFor2DigitId && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            <div className="relative w-full max-w-md transform rounded-lg bg-white px-6 py-6 shadow-xl transition-all">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Manage 2-Digit User ID
                </h3>
                <button
                  onClick={() => {
                    setShow2DigitIdModal(false);
                    setSelectedUserFor2DigitId(null);
                    setNew2DigitId("");
                  }}
                  className="rounded-md text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  User: <strong>{selectedUserFor2DigitId.full_name}</strong>
                </p>
                <label htmlFor="2digit-id" className="block text-sm font-medium text-gray-700 mb-2">
                  2-Digit User ID
                </label>
                <input
                  type="text"
                  id="2digit-id"
                  value={new2DigitId}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "").slice(0, 2);
                    setNew2DigitId(value);
                  }}
                  placeholder="e.g., 01"
                  className="block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500 sm:text-sm text-center"
                  maxLength={2}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter a 2-digit numeric ID (01-99)
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShow2DigitIdModal(false);
                    setSelectedUserFor2DigitId(null);
                    setNew2DigitId("");
                  }}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssign2DigitId}
                  disabled={!new2DigitId.trim() || new2DigitId.trim().length !== 2}
                  className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Management Modal */}
      {showPasswordModal && selectedUserForPassword && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            <div className="relative w-full max-w-md transform rounded-lg bg-white px-6 py-6 shadow-xl transition-all">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Set User Password
                </h3>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setSelectedUserForPassword(null);
                    setNewPassword("");
                  }}
                  className="rounded-md text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  User: <strong>{selectedUserForPassword.full_name}</strong>
                </p>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter password"
                  className="block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  This password will be used for admin functions when switching users
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setSelectedUserForPassword(null);
                    setNewPassword("");
                  }}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSetPassword}
                  disabled={!newPassword.trim()}
                  className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Set Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
